// ✅ Trekkin.app — PanelLiveShare.jsx v4.2.0 (PERMISSIONS READY + VIEWER SAFE)
// ---------------------------------------------------------------------------
// 🔹 Mantém 100% do layout v4.1.3
// 🔹 Adiciona sistema de permissões sem alterar UI
// 🔹 Viewer (?share=) ignora permissões (apenas visualização)
// 🔹 Admin bypass total através do useCan()
// ---------------------------------------------------------------------------

import { useEffect, useState, useMemo } from "react";
import { usePanelsStore } from "../stores/usePanelsStore.jsx";
import { useShareStore } from "../stores/useShareStore.jsx";
import { useAuthStore } from "../stores/useAuthStore.jsx";
import { useCan } from "../hooks/useCan.js";

import MiniMapRealTimeStatic from "./MiniMapRealTimeStatic.jsx";
import TrekkinChart from "./TrekkinChart.jsx";
import StatsPanelIcons from "./StatsPanelIcons.jsx";
import { showToast } from "./NotifyToast.jsx";
import { useTranslate } from "../i18n/useTranslate";

// ----------------------------
// Haversine
// ----------------------------
function haversine(a, b) {
  if (!a || !b) return 0;
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad((b.lat || 0) - (a.lat || 0));
  const dLon = toRad((b.lon || 0) - (a.lon || 0));
  const lat1 = toRad(a.lat || 0);
  const lat2 = toRad(b.lat || 0);

  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLon / 2);
  const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export default function PanelLiveShare({ shareId }) {
  const { resetToMap } = usePanelsStore();
  const { listenShare, liveData, liveStats, livePath, liveMeta } = useShareStore();
  const { user } = useAuthStore();
  const translate = useTranslate();

  const can = useCan(); // 🔐 sistema de permissões

  const [isMinimized, setIsMinimized] = useState(false);

  // Viewer mode → URL tem ?share=XYZ
  const isShareMode = window.location.search.includes("share=");

  const base = import.meta.env.BASE_URL || "/";

  // ----------------------------
  // PERMISSÕES
  // ----------------------------
  const canOpen = can("PanelLiveShare", "open");
  const canViewGroup = can("PanelLiveShare", "view_group_positions");
  const canToggleFollow = can("PanelLiveShare", "toggle_follow");

  // viewer mode nunca bloqueia
  const allow = (fn, msg) => {
    if (isShareMode) return true;
    if (!fn) {
      showToast(msg);
      return false;
    }
    return true;
  };

  // ----------------------------
  // Helpers
  // ----------------------------
  const toNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const formatTime = (sec) => {
    const s = Math.floor(toNumber(sec));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(
      2,
      "0"
    )}:${String(ss).padStart(2, "0")}`;
  };

  const formatTimeAgo = (ts) => {
    if (!ts) return "—";
    const diff = Math.floor((Date.now() - ts) / 1000);
    if (diff < 5) return translate("liveShare.timeAgo.justNow");
    if (diff < 60) return translate("liveShare.timeAgo.seconds", { n: diff });
    if (diff < 3600) {
      return translate("liveShare.timeAgo.minutes", { n: Math.floor(diff / 60) });
    }
    return translate("liveShare.timeAgo.hours", { n: Math.floor(diff / 3600) });
  };

  // ----------------------------
  // Listener LIVE
  // ----------------------------
  useEffect(() => {
    if (!allow(canOpen, translate("liveShare.noPermissionOpen"))) return;
    if (!shareId) return;
    listenShare(shareId);
  }, [shareId]);

  useEffect(() => {
    return () => document.body.classList.remove("panel-minimized");
  }, []);

  // ----------------------------
  // Dados LIVE
  // ----------------------------
  const stats = liveStats || {};
  const position = liveData || null;
  const path = livePath || [];
  const userInfo = liveMeta?.user || null;
  const isActive = liveMeta?.active ?? true;
  const lastUpdate = liveMeta?.lastUpdate || null;

  const coords = position
    ? {
        lat: toNumber(position.lat),
        lon: toNumber(position.lon),
        ele: toNumber(position.ele),
        acc: toNumber(position.acc),
        ts: position.ts || Date.now(),
      }
    : null;

  const altitudeValue = coords ? Math.round(coords.ele || 0) : 0;
  const altitudeLabel = `${altitudeValue} m`;

  // ----------------------------
  // Minimizar
  // ----------------------------
  const ToggleButton = ({ onClick }) => (
    <button
      onClick={onClick}
      style={{
        width: 28,
        height: 28,
        borderRadius: 999,
        border: "none",
        background: "#28a745",
        color: "#fff",
        fontSize: 18,
        fontWeight: 700,
      }}
    >
      {isMinimized ? "+" : "−"}
    </button>
  );

  const toggleMin = () =>
    setIsMinimized((v) => {
      const next = !v;
      if (next) document.body.classList.add("panel-minimized");
      else document.body.classList.remove("panel-minimized");
      return next;
    });

  // ----------------------------
  // Altimetria
  // ----------------------------
  const chartPoints = useMemo(() => {
    if (!Array.isArray(path) || path.length < 2) return [];
    let dist = 0;
    const out = [];
    for (let i = 1; i < path.length; i++) {
      const prev = path[i - 1];
      const cur = path[i];
      dist += haversine(prev, cur) / 1000;
      out.push({
        lat: cur.lat,
        lon: cur.lon,
        ele: cur.ele,
        distKm: dist,
      });
    }
    return out;
  }, [path]);

  // ----------------------------
  // Métricas LIVE (mesmo critério do PanelNav)
  // ----------------------------
  const derivedStatsFromPath = useMemo(() => {
    const pts = Array.isArray(path) ? path : [];
    if (pts.length < 2) return null;

    let distMeters = 0;
    let movingSec = 0;
    let dPlus = 0;

    const tsOf = (p) => Number(p?.ts || p?.time || 0);
    const firstTs = tsOf(pts[0]);
    const lastTs = tsOf(pts[pts.length - 1]);
    const totalSec =
      firstTs > 0 && lastTs >= firstTs ? (lastTs - firstTs) / 1000 : 0;

    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      const segDist = haversine(a, b);
      distMeters += segDist;

      const dt = (tsOf(b) - tsOf(a)) / 1000;
      if (dt > 0 && dt <= 300) {
        const kmh = (segDist / dt) * 3.6;
        if (kmh > 1.2) movingSec += dt;
      }

      const eleA = Number(a?.ele || 0);
      const eleB = Number(b?.ele || 0);
      const da = eleB - eleA;
      if (da > 0 && da < 15) dPlus += da;
    }

    const distKm = distMeters / 1000;
    const stoppedSec = Math.max(0, totalSec - movingSec);
    const avgMovingKmh = movingSec > 0 ? distKm / (movingSec / 3600) : 0;

    return {
      totalSec,
      movingSec,
      stoppedSec,
      distKm,
      dPlus,
      avgMovingKmh,
      points: pts.length,
    };
  }, [path]);

  const fallbackStats = useMemo(() => {
    const totalSec = toNumber(stats.totalTime ?? stats.elapsed ?? 0);
    const movingSec = toNumber(stats.movingTime ?? stats.moving ?? 0);
    const stoppedRaw = stats.stoppedTime ?? stats.pause;
    const stoppedSec = Number.isFinite(Number(stoppedRaw))
      ? toNumber(stoppedRaw)
      : Math.max(0, totalSec - movingSec);
    const distanceKm = Number.isFinite(Number(stats.distanceKm))
      ? Number(stats.distanceKm)
      : Number.isFinite(Number(stats.distance))
      ? Number(stats.distance) / 1000
      : 0;
    const dPlus = toNumber(stats.dPlus ?? stats.elevationGain ?? 0);
    const avgMovingKmh =
      movingSec > 0
        ? distanceKm / (movingSec / 3600)
        : toNumber(stats.currentSpeed ?? stats.speed ?? 0);
    const points = Number.isFinite(Number(stats.points))
      ? Number(stats.points)
      : path.length;

    return {
      totalSec,
      movingSec,
      stoppedSec,
      distKm: distanceKm,
      dPlus,
      avgMovingKmh,
      points,
    };
  }, [path.length, stats, toNumber]);

  const displayStats = derivedStatsFromPath || fallbackStats;
  const speedLabel = `${Number(displayStats?.avgMovingKmh || 0).toFixed(1)} km/h`;
  const dPlusLabel = `${Math.round(Number(displayStats?.dPlus || 0))} m`;
  const distanceLabel = `${Number(displayStats?.distKm || 0).toFixed(2)} km`;
  const pointsLabel = `${Math.round(Number(displayStats?.points || 0))} pts`;

  // ----------------------------
  // Navegação externa
  // ----------------------------
  const openExternalNav = () => {
    if (!coords?.lat || !coords?.lon) {
      showToast(translate("liveShare.noPositionYet"), "error");
      return;
    }

    const apple = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const url = apple
      ? `https://maps.apple.com/?daddr=${coords.lat},${coords.lon}&dirflg=d`
      : `https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lon}`;

    window.open(url, "_blank");
  };

  // ----------------------------
  // Render
  // ----------------------------
  const dynamicPadding = isMinimized
    ? "12px 18px 12px"
    : isShareMode
    ? "16px 18px 20px"
    : "16px 18px 90px";

  return (
    <div className="tk-live-share" style={{ padding: dynamicPadding, pointerEvents: "auto" }}>
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 8,
          alignItems: "center",
        }}
      >
        <h2 style={{ color: "#28a745", margin: 0 }}>{translate("liveShare.title")}</h2>
        <ToggleButton onClick={toggleMin} />
      </div>

      {!isActive && (
        <div style={{ fontSize: 11, color: "#dc3545", marginBottom: 6 }}>
          {translate("liveShare.ended")}
        </div>
      )}

      {/* PRIMEIRO CARTÃO */}
      <HeaderCard
        userInfo={userInfo}
        coords={coords}
        lastUpdate={lastUpdate}
        base={base}
        isActive={isActive}
        formatTimeAgo={formatTimeAgo}
        openExternalNav={openExternalNav}
      />

      {/* CONTEÚDO COMPLETO */}
      {!isMinimized && (
        <>
          {/* ⭐️ PAINEL OFICIAL 3×3 ⭐️ */}
          <StatsPanelIcons
            base={base}
            totalTime={displayStats?.totalSec ?? 0}
            stoppedTime={displayStats?.stoppedSec ?? 0}
            movingTime={displayStats?.movingSec ?? 0}
            speed={speedLabel}
            dPlus={dPlusLabel}
            distance={distanceLabel}
            altitude={altitudeLabel}
            coords={coords ? { lat: coords.lat, lon: coords.lon } : null}
            points={pointsLabel}
          />

          {/* ⭐️❗️ MINIMAPA — OCULTADO EM VIEWER MODE ❗️⭐️ */}
          {!isShareMode && coords && (
            <div style={{ marginTop: 10 }}>
              <MiniMapRealTimeStatic
                points={
                  path.length
                    ? path
                    : [{ lat: coords.lat, lon: coords.lon, ele: coords.ele }]
                }
                height={240}
                showUserMarker={true}
              />
            </div>
          )}

          {/* CHART */}
          {chartPoints.length > 1 && (
            <div className="tk-live-share-chart"
              style={{
                marginTop: 16,
                padding: "12px 14px 6px",
                borderRadius: 12,
                background: "rgba(255,255,255,0.95)",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {translate("liveShare.elevationProfile")}
              </div>
              <TrekkinChart points={chartPoints} height={120} padding={10} borderRadius={12} fillColor="rgba(40, 167, 69, 0.16)" showGrid showDistanceAxis />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ----------------------------
// HeaderCard (inalterado)
// ----------------------------
function HeaderCard({
  userInfo,
  coords,
  lastUpdate,
  isActive,
  base,
  formatTimeAgo,
  openExternalNav,
}) {
  return (
    <div className="tk-live-share-header"
      style={{
        background: "rgba(255,255,255,0.95)",
        borderRadius: 12,
        padding: "10px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              background: isActive ? "#28a745" : "#dc3545",
              borderRadius: 999,
            }}
          />
          <strong>{userInfo?.name || userInfo?.email || "—"}</strong>
        </div>

        <span style={{ fontSize: 11, color: "#666" }}>
          {formatTimeAgo(lastUpdate)}
        </span>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <span
          style={{ flex: 1, fontFamily: "monospace", fontSize: 11 }}
        >
          {coords
            ? `${coords.lat.toFixed(5)} / ${coords.lon.toFixed(5)}`
            : "— / —"}
        </span>

        <button
          onClick={openExternalNav}
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            border: "1px solid rgba(0,0,0,0.12)",
            background: "#fff",
          }}
        >
          <img src={`${base}icons/car-nav.svg`} style={{ width: 20 }} />
        </button>
      </div>
    </div>
  );
}
